<template>
  <v-dialog
    v-model="dialog"
    max-width="800px"
    scrollable
    persistent
  >
    <v-card>
      <v-card-title>
        <span class="headline">{{ modalTitle }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4">

        <!-- Add new relation -->
        <v-row align="center" class="mb-4">
          <v-col cols="9">
            <v-autocomplete
              v-model="selectedItemToAdd"
              :items="availableItems"
              :item-text="availableItemText"
              :item-value="availableItemValue"
              :label="addLabel"
              :loading="loadingAvailable"
              outlined
              dense
              clearable
              hide-details
              return-object
            ></v-autocomplete>
          </v-col>
          <v-col cols="2">
            <v-btn
              color="primary"
              :disabled="!selectedItemToAdd"
              :loading="adding"
              @click="addRelation"
              block
            >
              <v-icon left>mdi-link-plus</v-icon>
              {{ $t('add') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mb-4"></v-divider>

        <!-- Current relations table -->
        <v-data-table
          :headers="tableHeaders"
          :items="relatedItems"
          class="elevation-1 compact-table"
          :loading="loadingRelated"
          hide-default-footer
          disable-pagination
        >
          <template v-slot:[`item.active`]="{ item }">
            <v-chip small :color="item.active ? 'success' : 'error'" dark>
              {{ item.active ? $t('yes') : $t('no') }}
            </v-chip>
          </template>

          <template v-slot:[`item.price`]="{ item }">
            {{ formatPrice(item.price) }}
          </template>

          <template v-slot:[`item.icon`]="{ item }">
            <v-icon v-if="item.icon">{{ item.icon }}</v-icon>
            <span v-else>—</span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  :loading="removingId === getItemId(item)"
                  @click="removeRelation(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('remove_relation') }}</span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <span class="grey--text">{{ $t('no_items_found') }}</span>
          </template>
        </v-data-table>

      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text @click="closeModal">
          {{ $t('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
  ::v-deep .compact-table .v-data-table__wrapper td,
  ::v-deep .compact-table .v-data-table__wrapper th {
    padding: 6px 8px !important;
  }
</style>

<script>
import { mapActions } from 'vuex'
import AppActions from '@/store/app/actions-types'
import BillableAPI from '@/services/api/business/billable'
import DeliverableAPI from '@/services/api/business/deliverable'
import DeliverableBillableAPI from '@/services/api/business/deliverable-billable'

export default {
  name: 'DeliverableBillableModal',

  data() {
    return {
      dialog: false,
      mode: null, // 'from-deliverable' | 'from-billable'
      sourceItem: null,
      relatedItems: [],
      availableItems: [],
      selectedItemToAdd: null,
      loadingRelated: false,
      loadingAvailable: false,
      adding: false,
      removingId: null,
    }
  },

  computed: {
    modalTitle() {
      if (!this.sourceItem) return ''
      if (this.mode === 'from-deliverable') {
        return this.$t('manage_billables_for_deliverable', { title: this.sourceItem.deliverable_title })
      }
      return this.$t('manage_deliverables_for_billable', { title: this.sourceItem.billable_title })
    },

    addLabel() {
      return this.mode === 'from-deliverable'
        ? this.$t('select_billable_to_add')
        : this.$t('select_deliverable_to_add')
    },

    availableItemText() {
      return this.mode === 'from-deliverable' ? 'billable_title' : 'deliverable_title'
    },

    availableItemValue() {
      return this.mode === 'from-deliverable' ? 'billable_id' : 'deliverable_id'
    },

    tableHeaders() {
      if (this.mode === 'from-deliverable') {
        return [
          { text: this.$t('billable_title'), value: 'billable_title', sortable: true },
          { text: this.$t('unit_label'), value: 'unit_label', sortable: true },
          { text: this.$t('price'), value: 'price', sortable: true },
          { text: this.$t('active'), value: 'active', sortable: false },
          { text: this.$t('actions'), value: 'actions', sortable: false },
        ]
      }
      return [
        { text: this.$t('deliverable_title'), value: 'deliverable_title', sortable: true },
        { text: this.$t('icon'), value: 'icon', sortable: false },
        { text: this.$t('active'), value: 'active', sortable: false },
        { text: this.$t('actions'), value: 'actions', sortable: false },
      ]
    },
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
    ]),

    openModal(mode, sourceItem) {
      this.mode = mode
      this.sourceItem = sourceItem
      this.relatedItems = []
      this.availableItems = []
      this.selectedItemToAdd = null
      this.dialog = true
      this.loadData()
    },

    closeModal() {
      this.dialog = false
    },

    loadData() {
      this.loadRelated()
      this.loadAvailable()
    },

    loadRelated() {
      this.loadingRelated = true
      const sourceId = this.mode === 'from-deliverable'
        ? this.sourceItem.deliverable_id
        : this.sourceItem.billable_id

      const apiCall = this.mode === 'from-deliverable'
        ? BillableAPI.getAllBillablesByDeliverable(sourceId)
        : DeliverableAPI.getAllDeliverablesByBillable(sourceId)

      apiCall
        .then((result) => {
          this.relatedItems = result.data.items || result.data || []
          this.loadingRelated = false
          this.refreshAvailableFilter()
        })
        .catch(() => {
          this.loadingRelated = false
        })
    },

    loadAvailable() {
      const customerId = this.sourceItem.customer_id
      if (!customerId) return

      this.loadingAvailable = true

      const apiCall = this.mode === 'from-deliverable'
        ? BillableAPI.getAllBillablesByCustomer(customerId, { limit: 1000, offset: 0 })
        : DeliverableAPI.getAllDeliverablesByCustomer(customerId, { limit: 1000, offset: 0 })

      apiCall
        .then((result) => {
          this.allAvailable = result.data.items || result.data || []
          this.loadingAvailable = false
          this.refreshAvailableFilter()
        })
        .catch(() => {
          this.loadingAvailable = false
        })
    },

    refreshAvailableFilter() {
      if (!this.allAvailable) return

      const relatedIds = this.relatedItems.map(item => this.getItemId(item))

      this.availableItems = this.allAvailable.filter(item => {
        return !relatedIds.includes(this.getItemId(item))
      })
    },

    getItemId(item) {
      return this.mode === 'from-deliverable' ? item.billable_id : item.deliverable_id
    },

    addRelation() {
      if (!this.selectedItemToAdd) return

      this.adding = true
      const deliverableId = this.mode === 'from-deliverable'
        ? this.sourceItem.deliverable_id
        : this.selectedItemToAdd.deliverable_id

      const billableId = this.mode === 'from-deliverable'
        ? this.selectedItemToAdd.billable_id
        : this.sourceItem.billable_id

      DeliverableBillableAPI.createDeliverableBillable(deliverableId, billableId)
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('deliverable_billable_added_successfully'))
          this.selectedItemToAdd = null
          this.adding = false
          this.loadRelated()
        })
        .catch((error) => {
          this.adding = false
          console.error('Error adding relation:', error)
        })
    },

    removeRelation(item) {
      const itemId = this.getItemId(item)
      this.removingId = itemId

      const deliverableId = this.mode === 'from-deliverable'
        ? this.sourceItem.deliverable_id
        : item.deliverable_id

      const billableId = this.mode === 'from-deliverable'
        ? item.billable_id
        : this.sourceItem.billable_id

      DeliverableBillableAPI.deleteDeliverableBillable(deliverableId, billableId)
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('deliverable_billable_removed_successfully'))
          this.removingId = null
          this.loadRelated()
        })
        .catch((error) => {
          this.removingId = null
          console.error('Error removing relation:', error)
        })
    },

    normalizePrice(price) {
      if (price === null || price === undefined) return price

      const rawPrice = String(price).trim()
      if (rawPrice === '') return rawPrice

      const sanitizedPrice = rawPrice.replace(/\s/g, '')
      const lastComma = sanitizedPrice.lastIndexOf(',')
      const lastDot = sanitizedPrice.lastIndexOf('.')

      if (lastComma !== -1 && lastDot !== -1) {
        const decimalSeparator = lastComma > lastDot ? ',' : '.'
        const thousandsSeparator = decimalSeparator === ',' ? '.' : ','

        return sanitizedPrice
          .split(thousandsSeparator)
          .join('')
          .replace(decimalSeparator, '.')
      }

      if (lastComma !== -1) {
        const integerPart = sanitizedPrice.slice(0, lastComma).replace(/,/g, '')
        const decimalPart = sanitizedPrice.slice(lastComma + 1).replace(/,/g, '')
        return `${integerPart}.${decimalPart}`
      }

      return sanitizedPrice
    },

    formatPrice(price) {
      if (price === null || price === undefined) return ''

      const normalizedPrice = this.normalizePrice(price)
      if (normalizedPrice === '') return ''

      const parsedPrice = Number(normalizedPrice)
      if (Number.isNaN(parsedPrice)) return price

      return parsedPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
  },
}
</script>
